"use client";

import React, { forwardRef, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { SelectOption } from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface DatePickerInputProps {
  value: string;
  onClick: () => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

interface CustomDatePickerProps {
  selected: Date | null;
  onChange: (date: Date | null) => void;
  placeholderText?: string;
  className?: string;
  disabled?: boolean;
  minDate?: Date;
  maxDate?: Date;
  showTimeSelect?: boolean;
  timeFormat?: string;
  dateFormat?: string;
  isClearable?: boolean;
}

interface RenderCustomHeaderProps {
  date: Date;
  changeYear: (year: number) => void;
  changeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}

// Custom input component for the date picker
const DatePickerInput = forwardRef<HTMLButtonElement, DatePickerInputProps>(
  (
    {
      value,
      onClick,
      placeholder = "Select date",
      className = "",
      disabled = false,
    },
    ref
  ) => (
    <button
      type='button'
      onClick={onClick}
      ref={ref}
      disabled={disabled}
      className={`flex items-center w-full px-3 py-2 text-left border border-input rounded-lg shadow-sm ${
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "cursor-pointer hover:bg-muted"
      } ${className}`}
    >
      {value || <span className='text-muted-foreground'>{placeholder}</span>}
      <ChevronDown className='ml-auto h-4 w-4 opacity-50' />
    </button>
  )
);

DatePickerInput.displayName = "DatePickerInput";

// Custom component to handle date picker select dropdown
const DatePickerSelect = ({
  options,
  selected,
  onChange,
  placeholder,
  className,
}: {
  options: SelectOption[];
  selected: SelectOption | null;
  onChange: (option: SelectOption) => void;
  placeholder: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const isMobile = useIsMobile();
  const showSearch = options.length > 6;

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelect = (option: SelectOption) => {
    onChange(option);
    setOpen(false);
    setSearchQuery("");
  };

  const triggerButton = (
    <Button
      variant='outline'
      role='combobox'
      aria-expanded={open}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        setOpen(!open);
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      className={cn(
        "w-full justify-between bg-background font-normal",
        className
      )}
    >
      <div className='flex items-center gap-2'>
        {selected ? (
          <span className='truncate'>{selected.label}</span>
        ) : (
          <span className='text-muted-foreground truncate'>{placeholder}</span>
        )}
      </div>
      <ChevronDown className='h-4 w-4 opacity-50' />
    </Button>
  );

  const commandContent = (
    <Command
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {showSearch && (
        <CommandInput
          placeholder='Search...'
          value={searchQuery}
          onValueChange={setSearchQuery}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className={cn(isMobile && "text-base")}
        />
      )}
      <CommandList>
        {filteredOptions.length === 0 && (
          <CommandEmpty>No options found.</CommandEmpty>
        )}
        <CommandGroup>
          {filteredOptions.map((option) => {
            const isSelected = selected?.value === option.value;
            return (
              <CommandItem
                key={option.value}
                value={option.label}
                onSelect={() => handleSelect(option)}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(option);
                }}
                className='flex items-center border-b border-muted/40 md:gap-4 py-4 lg:py-2'
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    isSelected ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className='truncate'>{option.label}</span>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
    </Command>
  );

  return (
    <div
      className='datepicker-select'
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
        }}
      >
        <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        <PopoverContent
          className='datepicker-select-popover w-full p-0'
          style={{ zIndex: 1000000 }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {commandContent}
        </PopoverContent>
      </Popover>
    </div>
  );
};

// Add custom CSS to handle dark mode and z-index
const datePickerStyles = `
  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 40 !important;
  }
  
  /* Higher z-index for our custom select within the datepicker */
  .datepicker-select-popover {
    z-index: 1000000 !important;
  }

  /* Make all Popover content within datepicker go on top */
  #date-picker-portal .react-datepicker__header {
    z-index: 10 !important;
  }

  #date-picker-portal div[data-radix-popper-content-wrapper] {
    z-index: 1000000 !important;
  }

  .react-datepicker {
    font-family: inherit !important;
    font-size: 0.9rem !important;
    background-color: hsl(var(--background)) !important;
    color: hsl(var(--foreground)) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: var(--radius) !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
  }
  
  .react-datepicker__header {
    background-color: hsl(var(--muted)) !important;
    border-bottom: 1px solid hsl(var(--border)) !important;
    padding: 10px 0 !important;
  }
  
  .react-datepicker__current-month {
    font-weight: 600 !important;
    color: hsl(var(--foreground)) !important;
    margin-bottom: 4px !important;
  }
  
  .react-datepicker__day-name {
    color: hsl(var(--muted-foreground)) !important;
    font-weight: 500 !important;
    width: 2rem !important;
    margin: 0.2rem !important;
  }
  
  .react-datepicker__day {
    color: hsl(var(--foreground)) !important;
    width: 2rem !important;
    height: 2rem !important;
    line-height: 2rem !important;
    margin: 0.2rem !important;
    border-radius: 0.5rem !important;
  }
  
  .react-datepicker__day:hover {
    background-color: hsl(var(--accent)) !important;
  }
  
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: hsl(var(--primary)) !important;
    color: hsl(var(--primary-foreground)) !important;
    font-weight: 600 !important;
  }
  
  .react-datepicker__day--disabled {
    color: hsl(var(--muted-foreground)) !important;
    opacity: 0.4 !important;
  }
  
  .react-datepicker__navigation {
    top: 12px !important;
    background: none !important;
    border: none !important;
  }

  .react-datepicker__navigation--previous {
    left: 12px !important;
  }

  .react-datepicker__navigation--next {
    right: 12px !important;
  }

  .react-datepicker__navigation-icon::before {
    border-color: hsl(var(--foreground)) !important;
  }

  /* Dropdown Styling */
  .react-datepicker__month-dropdown-container,
  .react-datepicker__year-dropdown-container {
    margin: 0 5px !important;
  }
  
  .react-datepicker__month-read-view,
  .react-datepicker__year-read-view {
    background-color: hsl(var(--secondary)) !important;
    border-radius: var(--radius) !important;
    padding: 2px 8px !important;
    font-weight: 500 !important;
    color: hsl(var(--secondary-foreground)) !important;
  }
  
  .react-datepicker__month-read-view:hover,
  .react-datepicker__year-read-view:hover {
    background-color: hsl(var(--secondary-foreground/10%)) !important;
  }

  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: hsl(var(--background)) !important;
    border: 1px solid hsl(var(--border)) !important;
    border-radius: var(--radius) !important;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important;
    padding: 0.5rem !important;
  }

  .react-datepicker__month-option,
  .react-datepicker__year-option {
    padding: 0.5rem !important;
    color: hsl(var(--foreground)) !important;
    border-radius: var(--radius) !important;
  }

  .react-datepicker__month-option:hover,
  .react-datepicker__year-option:hover {
    background-color: hsl(var(--accent)) !important;
  }

  .react-datepicker__month-option--selected,
  .react-datepicker__year-option--selected {
    background-color: hsl(var(--primary)) !important;
    color: hsl(var(--primary-foreground)) !important;
  }
`;

const CustomDatePicker: React.FC<CustomDatePickerProps> = ({
  selected,
  onChange,
  placeholderText = "Select date",
  className = "",
  disabled = false,
  minDate,
  maxDate,
  showTimeSelect = false,
  timeFormat = "HH:mm",
  dateFormat = showTimeSelect ? "MMMM d, yyyy h:mm aa" : "MMMM d, yyyy",
  isClearable = true,
}) => {
  const isMobile = useIsMobile();

  // Custom navigation components
  const renderCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: RenderCustomHeaderProps) => {
    const years: SelectOption[] = Array.from({ length: 20 }, (_, i) => {
      const year = new Date().getFullYear() - 10 + i;
      return { value: year.toString(), label: year.toString() };
    });

    const months: SelectOption[] = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ].map((month, i) => ({
      value: i.toString(),
      label: month,
    }));

    const selectedMonth = months[date.getMonth()] || null;
    const selectedYear =
      years.find((y) => y.value === date.getFullYear().toString()) || null;

    return (
      <div
        className='flex items-center justify-between px-2 py-2'
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            decreaseMonth();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={prevMonthButtonDisabled}
          type='button'
          className='p-1 rounded-full hover:bg-muted'
        >
          <ChevronLeft className='h-4 w-4' />
        </button>

        <div
          className='flex gap-2 flex-1 px-2'
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <DatePickerSelect
            options={months}
            selected={selectedMonth}
            onChange={(option) => {
              changeMonth(Number(option.value));
            }}
            placeholder='Month'
            className='min-w-[120px]'
          />
          <DatePickerSelect
            options={years}
            selected={selectedYear}
            onChange={(option) => {
              changeYear(Number(option.value));
            }}
            placeholder='Year'
            className='min-w-[100px]'
          />
        </div>

        <button
          onClick={(e) => {
            e.stopPropagation();
            increaseMonth();
          }}
          onMouseDown={(e) => e.stopPropagation()}
          disabled={nextMonthButtonDisabled}
          type='button'
          className='p-1 rounded-full hover:bg-muted'
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      </div>
    );
  };

  return (
    <>
      <style>{datePickerStyles}</style>
      <DatePicker
        selected={selected}
        onChange={onChange}
        customInput={
          <DatePickerInput
            value={selected ? format(selected, dateFormat) : ""}
            onClick={() => {}}
            placeholder={placeholderText}
            className={className}
            disabled={disabled}
          />
        }
        placeholderText={placeholderText}
        disabled={disabled}
        minDate={minDate}
        maxDate={maxDate}
        showTimeSelect={showTimeSelect}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        isClearable={isClearable}
        popperClassName='react-datepicker-popper'
        calendarClassName='shadow-lg'
        wrapperClassName='w-full'
        renderCustomHeader={renderCustomHeader}
        popperProps={{
          strategy: "fixed",
        }}
        withPortal={isMobile}
        portalId='date-picker-portal'
      />
    </>
  );
};

export default CustomDatePicker;
